import "./styles.scss";
import React, { useEffect, useRef, useState } from "react";
import moment from "moment";
import queryString from "query-string";
import sweetAlert from "sweetalert";

import {
  Breadcrumbs,
  Button,
  Loading,
  Page,
  Pagination,
  Panel,
} from "components";
import { Filters } from "./components";

import pro from "services/pro";
import vouchers from "services/vouchers";
import { format } from "date-fns";
import { isCpfValid } from "utils/validator";
import { formatCpf, removeTimeFromDate } from "utils/formatters";
import fileDownload from "js-file-download";

const breadcrumbs = [
  { name: "Home", to: "/" },
  { name: "PRO", to: "/pro" },
  { name: "Listagem de Cashbacks Automáticos", to: "" },
];

const initialState = {
  cpf: "",
  startAt: null,
  endAt: null,
  cashbackReferenceMonth: null,
};

export function CashbackAutomatic(props) {
  const [loading, setLoading] = useState(true);
  const [cashbacks, setCashbacks] = useState([]);
  const [totalCashbacks, setTotalCashbacks] = useState(0);
  const [pagination, setPagination] = useState(getCurrentPagination());
  const [filters, setFilters] = useState(initialState);
  const [openedDetails, setOpenedDetails] = useState(null);
  const [loadingOpenDetails, setLoadingOpenDetails] = useState(false);
  const [voucherData, setVoucherData] = useState([]);
  const [areFiltersActive, setAreFiltersActive] = useState(false);

  const debounceTimer = useRef(null);

  const handleExport = () => {
    vouchers
      .get(`/cashbacks/_export`, {
        responseType: "blob",
        params: {
          cashbackReferenceMonth: filters.cashbackReferenceMonth,
        },
      })
      .then((response) => {
        fileDownload(response.data, "cashback-gerados-automaticamente.csv");
      })
      .catch((error) => {
        console.error(error);
      });
  };

  function handleOpenDetails(cashbackId) {
    if (openedDetails === cashbackId) {
      setOpenedDetails(null);
      setVoucherData([]);
      return;
    }

    fetchGetVouchersGeneratedByCashbackId(cashbackId);
    setOpenedDetails(cashbackId);
  }

  function fetchGetVouchersGeneratedByCashbackId(cashbackId) {
    setLoadingOpenDetails(true);
    setVoucherData([]);

    vouchers
      .get(`/cashbacks/${cashbackId}/inhabitant-vouchers`)
      .then(({ data }) => setVoucherData(data))
      .catch((error) => {
        console.error(error);
      })
      .finally(() => setLoadingOpenDetails(false));
  }

  function fetchData() {
    setLoading(true);

    const options = {
      params: {
        per: pagination.per,
        page: pagination.page,
        startAt: filters.startAt,
        endAt: filters.endAt,
        cpf: formatCpf(filters.cpf) || null,
        cashbackReferenceMonth: removeTimeFromDate(
          filters.cashbackReferenceMonth
        ),
      },
    };

    pro
      .get("cashbacks", options)
      .then(({ data }) => {
        if (!data || !data.data) {
          return;
        }

        if (Array.isArray(data.data)) {
          setCashbacks(data.data);
        }

        if (data.meta && data.meta.total_count) {
          setTotalCashbacks(data.meta.total_count);
        }
      })
      .catch((error) => {
        sweetAlert(
          "Algo deu errado!",
          "Não foi possível buscar os dados. Por favor, recarregue a página ou tente fazer o login novamente.",
          "error"
        );
        console.error(error);
      })
      .finally(() => setLoading(false));
  }

  function getCurrentPagination() {
    const params = queryString.parse(props.location.search);
    const page = (params.page && Number(params.page)) || 1;
    const per = (params.per && Number(params.per)) || 10;
    return { page, per };
  }

  function handleFiltersChange(name, value) {
    if (name === "startAt" && !value) {
      return setFilters((current) => ({
        ...current,
        startAt: null,
        endAt: null,
      }));
    }

    setFilters((current) => ({ ...current, [name]: value }));
  }

  function handleFiltersClean(event) {
    event.preventDefault();
    setFilters(initialState);
  }

  useEffect(() => {
    const noCpfProvided = !filters.cpf;
    const validCpf = filters.cpf && isCpfValid(filters.cpf);

    if (noCpfProvided || validCpf) {
      clearTimeout(debounceTimer.current);
      debounceTimer.current = setTimeout(() => {
        fetchData();
      }, 500);
    }
  }, [filters, pagination]);

  useEffect(() => {
    const { page, per } = getCurrentPagination();

    if (page !== pagination.page || per !== pagination.per) {
      setPagination({ per, page });
    }
  }, [props.location.search]);

  useEffect(() => {
    setAreFiltersActive(Boolean(filters.cashbackReferenceMonth));
  }, [filters.cashbackReferenceMonth]);

  return (
    <Page>
      <Breadcrumbs breadcrumbs={breadcrumbs} />

      <Page.Title>Listagem de Cashbacks</Page.Title>

      <Filters
        filters={filters}
        onChange={handleFiltersChange}
        onClean={handleFiltersClean}
      />

      <Panel className="panel-list">
        <div className="cashback-options">
          <p>
            Exibindo {cashbacks.length} de {totalCashbacks} cashbacks
          </p>
        </div>

        <>
          <table className="table table-curved">
            <thead>
              <tr>
                <th>Cliente</th>
                <th>CPF</th>
                <th>Valor da compra</th>
                <th>Mês de referência</th>
                <th>Ação</th>
              </tr>
            </thead>

            <tbody>
              {loading ? (
                <Loading type="page" size="large" />
              ) : !cashbacks.length ? (
                <tr>
                  <td className="text-center" colSpan="10">
                    Nenhum cashback encontrado.
                  </td>
                </tr>
              ) : (
                cashbacks.map((cashback, index) => (
                  <React.Fragment key={cashback.id}>
                    <tr>
                      <td>{cashback?.inhabitantName ?? 'Nome não informado'}</td>
                      <td>{cashback?.inhabitantCpf ? formatCpf(cashback?.inhabitantCpf) : 'CPF não informado'}</td>
                      <td>
                        {cashback.cashback.toLocaleString("pt-BR", {
                          style: "currency",
                          currency: "BRL",
                        })}
                      </td>
                      <td>
                        {cashback.month}/{cashback.year}
                      </td>
                      <td>
                        <Button
                          className="button-icon"
                          onClick={() => handleOpenDetails(cashback.id)}
                        >
                          <span className="glyph glyph-eye" />
                        </Button>
                      </td>
                    </tr>
                    {openedDetails === cashback.id && (
                      <tr>
                        <td colSpan="10">
                          {loadingOpenDetails ? (
                            <Loading type="page" size="large" />
                          ) : (
                            <div className="container-show-details">
                              <ul>
                                <li>
                                  Vouchers processados em:{" "}
                                  {voucherData.processedAt
                                    ? format(
                                        new Date(voucherData.processedAt),
                                        "dd/MM/yyyy HH:mm"
                                      )
                                    : "Ainda não processado!"}
                                </li>
                                {voucherData.vouchersGenerated?.length > 0 ? (
                                  voucherData.vouchersGenerated.map(
                                    (voucher) => (
                                      <li key={voucher.id}>
                                        R$ {voucher.discount} |{" "}
                                        {voucher.inhabitantVoucherId}
                                      </li>
                                    )
                                  )
                                ) : (
                                  <li>Nenhum voucher gerado</li>
                                )}
                              </ul>
                            </div>
                          )}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))
              )}
            </tbody>

            <tfoot>
              <tr>
                <th colSpan="8" />
              </tr>
            </tfoot>
          </table>

          <Pagination
            current={pagination.page}
            per={pagination.per}
            total={totalCashbacks}
            {...props}
          />

          <Button
            className="button-export"
            disabled={cashbacks.length === 0 || !areFiltersActive}
            onClick={handleExport}
          >
            Exportar
          </Button>
        </>
      </Panel>
    </Page>
  );
}
